/**
 * Global quick-add task. Floating + button in the header — and a
 * `c` keyboard shortcut (for "capture") — opens a tiny modal where
 * you can type a title and pick a work level.
 *
 * The whole point is to lower the activation energy of dumping
 * something out of your head onto your calendar. No notes, no
 * estimate slider — just title + level + Enter.
 *
 * Two affordances on submit:
 *   - Auto-schedule (default) — runs the scheduler. Behaviour
 *     follows the user's pref for that level (always / ask).
 *   - Inbox only — saves with defer=true. Useful when you're
 *     brainstorming and don't want every thought instantly on
 *     today's calendar.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { IconPlus } from './Icon';

export function QuickAddTaskButton() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // 'c' to capture. Ignore when typing in a field, when modifier
      // keys are involved, or when a dialog is already open.
      if (e.key !== 'c' || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      )
        return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setOpen(true);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        className="btn btn-accent inline-flex items-center gap-1 !px-2.5 !py-1.5 text-xs"
        onClick={() => setOpen(true)}
        title="Quick-add task (press c)"
        aria-label="Quick-add task"
      >
        <IconPlus size={14} />
        <span className="hidden sm:inline">Task</span>
      </button>
      {open && <QuickAddModal onClose={() => setOpen(false)} />}
    </>
  );
}

function QuickAddModal({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [workLevel, setWorkLevel] = useState<'deep' | 'shallow' | 'admin'>('shallow');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  async function submit(defer = false): Promise<void> {
    const trimmed = title.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const r = await api.createPersonalTask({ title: trimmed, workLevel, defer });
      window.dispatchEvent(new Event('synfolio:tasks-changed'));
      if (r.scheduled) {
        if (r.mode === 'always') {
          toast({
            tone: 'success',
            title: 'Scheduled',
            body: `${trimmed} is on your calendar.`,
          });
        } else {
          // Proposed — ProposalWatcher picks it up via the event.
          window.dispatchEvent(new Event('synfolio:proposal-changed'));
        }
      } else if (r.reason === 'no-working-hours') {
        toast({
          tone: 'warning',
          title: 'Saved to inbox',
          body: 'Set working hours in Profile so we can auto-schedule.',
        });
      } else if (r.reason === 'no-room') {
        toast({
          tone: 'warning',
          title: 'Saved to inbox',
          body: 'No slot fits — try widening your hours or shrinking the task.',
        });
      } else {
        toast({ title: 'Saved to inbox' });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="New task" maxWidth={420}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(false);
        }}
      >
        <input
          ref={inputRef}
          className="input text-base"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What do you need to do?"
          maxLength={200}
          required
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-cream-3">
            Work level
          </span>
          <div className="grid grid-cols-3 gap-1.5">
            {(
              [
                { id: 'deep', label: 'Deep', hint: '90+ min' },
                { id: 'shallow', label: 'Shallow', hint: '30+ min' },
                { id: 'admin', label: 'Admin', hint: '15 min' },
              ] as const
            ).map((lv) => (
              <button
                key={lv.id}
                type="button"
                onClick={() => setWorkLevel(lv.id)}
                className={`flex flex-col items-start gap-0.5 rounded-lg border p-2 text-left text-sm transition ${
                  workLevel === lv.id
                    ? 'border-clove bg-clove/10 text-cream'
                    : 'border-ink-rim text-cream-2 hover:bg-ink-3'
                }`}
              >
                <span className="font-medium">{lv.label}</span>
                <span className="text-[10px] text-cream-3">{lv.hint}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => void submit(true)}
            disabled={!title.trim() || busy}
          >
            Inbox only
          </button>
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-ghost text-xs" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-accent text-xs"
              disabled={!title.trim() || busy}
            >
              {busy ? 'Saving…' : 'Schedule'}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-cream-3">
          Tip: hit <kbd className="rounded bg-ink-3 px-1 py-0.5 font-mono text-[10px] text-cream-2">c</kbd>{' '}
          anywhere in the app to capture a task quickly.
        </p>
      </form>
    </Modal>
  );
}
